import { useQuery } from '@tanstack/react-query'
import { Vehicle } from '@otw/models'
import { db } from '@otw/models/db'
import { getDocs, query, where } from 'firebase/firestore'

const SOLD_STATUSES = new Set(['Sold', 'Sold elsewhere'])
const STALE_MS = 1000 * 60 * 5
const IN_QUERY_LIMIT = 10

const isSoldVehicle = (vehicle: Vehicle): boolean =>
  SOLD_STATUSES.has(vehicle.status ?? '') && Boolean(vehicle.sold_to_user)

const sortBySoldDateDesc = (vehicles: Vehicle[]): Vehicle[] =>
  [...vehicles].sort((a, b) => {
    const aTime = a.sold_date ? new Date(a.sold_date).getTime() : 0
    const bTime = b.sold_date ? new Date(b.sold_date).getTime() : 0
    return bTime - aTime
  })

const chunkIds = (ids: string[]): string[][] => {
  const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))]
  const chunks: string[][] = []
  for (let i = 0; i < unique.length; i += IN_QUERY_LIMIT) {
    chunks.push(unique.slice(i, i + IN_QUERY_LIMIT))
  }
  return chunks
}

const fetchSoldVehiclesByField = async (
  fieldPath: 'sold_to_contact_id' | 'sold_to_user.id',
  ids: string[],
): Promise<Vehicle[]> => {
  const chunks = chunkIds(ids)
  if (!chunks.length) return []

  const byId = new Map<string, Vehicle>()
  await Promise.all(
    chunks.map(async (chunk) => {
      const snapshot = await getDocs(
        query(db.master_inventory, where(fieldPath, 'in', chunk)),
      )
      for (const docSnap of snapshot.docs) {
        const vehicle = docSnap.data()
        if (isSoldVehicle(vehicle)) {
          byId.set(docSnap.id, { ...vehicle, id: docSnap.id })
        }
      }
    }),
  )

  return [...byId.values()]
}

export type SoldVehiclesForCrmInput = {
  /** Preferred: CRM contact doc ids. */
  contactIds?: string[]
  /** Legacy fallback: portal user ids on sold_to_user.id. */
  userIds?: string[]
}

export const soldVehiclesForCrmQueryKey = (input: SoldVehiclesForCrmInput) =>
  [
    'master_inventory',
    'sold_for_crm',
    'contacts',
    ...chunkIds(input.contactIds ?? []).flat(),
    'users',
    ...chunkIds(input.userIds ?? []).flat(),
  ] as const

/** Sold trucks for CRM detail pages — contact id first, portal user id fallback. */
export const useSoldVehiclesForCrm = (input: SoldVehiclesForCrmInput | undefined) => {
  const contactIds = (input?.contactIds ?? []).map((id) => id.trim()).filter(Boolean)
  const userIds = (input?.userIds ?? []).map((id) => id.trim()).filter(Boolean)
  const enabled = contactIds.length > 0 || userIds.length > 0

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: soldVehiclesForCrmQueryKey({ contactIds, userIds }),
    queryFn: async () => {
      const byId = new Map<string, Vehicle>()
      const [byContact, byUser] = await Promise.all([
        fetchSoldVehiclesByField('sold_to_contact_id', contactIds),
        fetchSoldVehiclesByField('sold_to_user.id', userIds),
      ])
      for (const vehicle of byContact) byId.set(vehicle.id, vehicle)
      for (const vehicle of byUser) {
        if (!byId.has(vehicle.id)) byId.set(vehicle.id, vehicle)
      }
      return sortBySoldDateDesc([...byId.values()])
    },
    enabled,
    staleTime: STALE_MS,
  })

  return {
    soldVehicles: data ?? [],
    isLoading: enabled && isLoading,
    isFetching: enabled && isFetching,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  }
}
